import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "leadDetailPanelState";

// Get initial panel state from localStorage or defaults
const getInitialPanelState = (panelId, defaultOpen = true) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      if (typeof state[panelId] === "boolean") {
        return state[panelId];
      }
    }
  } catch (e) {
    console.warn("Failed to read panel state from localStorage", e);
  }
  return defaultOpen;
};

// Save panel state to localStorage
const savePanelState = (panelId, isOpen) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const state = stored ? JSON.parse(stored) : {};
    state[panelId] = isOpen;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save panel state to localStorage", e);
  }
};

export default function CollapsiblePanel({
  id,
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  defaultOpenMobile = false,
  className,
  headerClassName,
  contentClassName,
  badge,
  "data-testid": testId,
}) {
  // Check if mobile on mount
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Determine initial state based on device and localStorage
  const getDefaultState = () => {
    // First check localStorage
    const storedState = getInitialPanelState(id, null);
    if (storedState !== null) {
      return storedState;
    }
    // Otherwise use device-specific defaults
    return isMobile ? defaultOpenMobile : defaultOpen;
  };

  const [isOpen, setIsOpen] = useState(getDefaultState);

  // Update state when mobile status changes (for initial load)
  useEffect(() => {
    const storedState = getInitialPanelState(id, null);
    if (storedState === null) {
      setIsOpen(isMobile ? defaultOpenMobile : defaultOpen);
    }
  }, [isMobile, id, defaultOpen, defaultOpenMobile]);

  const togglePanel = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    savePanelState(id, newState);
  };

  return (
    <Card className={cn("collapsible-panel", className)} data-testid={testId}>
      <CardHeader 
        className={cn(
          "pb-3 cursor-pointer select-none hover:bg-muted/50 transition-colors rounded-t-lg",
          !isOpen && "rounded-b-lg",
          headerClassName
        )}
        onClick={togglePanel}
        data-testid={`${testId}-header`}
      >
        <div className="flex items-center justify-between w-full">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            {Icon && <Icon size={18} className="text-primary" />}
            {title}
            {badge}
          </CardTitle>
          <div className="flex items-center gap-2">
            {isOpen ? (
              <ChevronUp size={18} className="text-muted-foreground transition-transform" />
            ) : (
              <ChevronDown size={18} className="text-muted-foreground transition-transform" />
            )}
          </div>
        </div>
      </CardHeader>
      <div 
        className={cn(
          "collapsible-content overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <CardContent className={cn("pt-0", contentClassName)}>
          {children}
        </CardContent>
      </div>
    </Card>
  );
}

// Hook for external control of panel state
export function usePanelState(panelId) {
  const [isOpen, setIsOpen] = useState(() => getInitialPanelState(panelId, true));

  const toggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    savePanelState(panelId, newState);
  };

  const open = () => {
    setIsOpen(true);
    savePanelState(panelId, true);
  };

  const close = () => {
    setIsOpen(false);
    savePanelState(panelId, false);
  };

  return { isOpen, toggle, open, close };
}
